import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes } from 'src/router/Routes';
import { LinkItem } from '../ui/LinkItem';
import { useAppConfig } from 'src/hooks/useAppConfig';
import { AssetTypeConditional } from '../user/AssetTypeConditional';
import { TSecurityType } from '@brickwise/asset-validation/dist/schema/enums';
import { checkIfCapitalGainsTaxesApply } from '../payment/rent/TaxSystem';

interface Props {
  assetId: string;
  active: 'asset' | 'investment' | 'notary' | 'forum' | 'records' | 'payout' | 'yearlyTaxReport' | 'serial-number' | 'preorder';
  showSecondaryItems?: boolean;
  isPrimary: boolean;
  isSecondary?: boolean;
  isPreorder?: boolean;
  isKESt?: boolean;
  securityType?: TSecurityType;
  useSerialNumber?: boolean;
}

export const AssetDetailsLinkList: FC<Props> = ({
  assetId,
  active,
  isPrimary,
  isSecondary,
  isPreorder = false,
  securityType,
  useSerialNumber = true,
}) => {
  const { t } = useTranslation();
  const appConfig = useAppConfig();
  const isKESt = checkIfCapitalGainsTaxesApply(securityType);

  return (
    <div style={{ display: 'flex', columnGap: '16px' }}>
      <LinkItem
        href={Routes.ASSETS.EDIT.replace(':assetId', assetId)}
        label={t('dashboard.assets.addedit.link.asset')}
        active={active === 'asset'}
      />
      <LinkItem
        href={Routes.ASSETS.INVESTMENT.replace(':assetId', assetId)}
        label={t('dashboard.assets.addedit.link.investment')}
        active={active === 'investment'}
      />
      <AssetTypeConditional supported={'builderOwnerModel'}>
        <LinkItem
          href={Routes.ASSETS.NOTARY.replace(':assetId', assetId)}
          label={t('dashboard.assets.addedit.link.notary')}
          active={active === 'notary'}
        />
      </AssetTypeConditional>
      {useSerialNumber ? (
        <LinkItem
          href={Routes.ASSETS.SERIAL_NUMBER.replace(':assetId', assetId)}
          label={t('dashboard.assets.addedit.link.serialNumber')}
          active={active === 'serial-number'}
        />
      ) : null}
      {isPreorder && (
        <LinkItem
          href={Routes.ASSETS.PREORDER.replace(':assetId', assetId)}
          label={t('dashboard.assets.addedit.link.preorders')}
          active={active === 'preorder'}
        />
      )}
      {(isPrimary || isSecondary) && (
        <LinkItem
          href={Routes.ASSETS.RECORDS.replace(':assetId', assetId)}
          label={t('dashboard.assets.addedit.link.records')}
          active={active === 'records'}
        />
      )}
      {isSecondary && (
        <>
          <LinkItem
            href={Routes.ASSETS.FORUM.replace(':assetId', assetId)}
            label={t('dashboard.assets.addedit.link.forum')}
            active={active === 'forum'}
          />

          {!appConfig?.featureFlags?.manualPayment ? (
            <LinkItem
              href={Routes.ASSETS.INVESTORPAYOUT.replace(':assetId', assetId)}
              label={t('dashboard.assets.addedit.link.payout')}
              active={active === 'payout'}
            />
          ) : null}
        </>
      )}
      {isSecondary && !isKESt && (
        <LinkItem
          href={Routes.ASSETS.YEARLYTAXREPORT.replace(':assetId', assetId)}
          label={t('dashboard.assets.addedit.link.yearlyTaxReport')}
          active={active === 'yearlyTaxReport'}
        />
      )}
    </div>
  );
};
